<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { ArrowPathIcon } from '@heroicons/vue/24/solid';
import { Button } from '@/components/ui/button';
import Select from '@/components/Form/Select.vue';
import { useSearchApi } from '@/composables/api/SearchApi';
import { useCursorPager } from '@/composables/useCursorPager';
import {
  useHistoryInspector,
  scopeForUser,
  HISTORY_ENTITY_SLUG,
} from '@/composables/useHistoryInspector';
import { usePermissionUsers } from '@/composables/api/usePermissionsApi';
import { useAuthStore } from '@/stores/authStore';
import LiveTrackActionCard from '@/components/HistoryInspector/LiveTrackActionCard.vue';
import type { IScope } from '@/types/filterBuilderTypes';
import type { LiveTrackAction } from '@/ts';

/**
 * Body of the History Inspector (SAU-138). Mounted inside the inspector panel
 * by `useHistoryInspector`. Lists `LiveTrackAction` records via the existing ES
 * search endpoint, newest first.
 *
 * Two modes:
 *   - `user`  — a dropdown picks whose history to show (defaults to
 *     `initialUserId`, the current user).
 *   - `track` — scoped to one live track (`initialFilter`); clicking a card's
 *     track drills further into that track.
 */
const props = defineProps<{
  seedKind: 'user' | 'track';
  /** User-mode: the user the dropdown starts on (the current user). */
  initialUserId?: number;
  /** Track-mode: the seed scope for one live track. */
  initialFilter?: IScope;
  /**
   * Shown inside a record's tab, such as a track's Actions tab, rather than on
   * its own in the peek. It then sits in the tab's normal flow, without the
   * pull into the peek's top padding, which put the count over the tabs.
   */
  embedded?: boolean;
}>();

const { openTrackHistory } = useHistoryInspector();

const selectedUserId = ref<number | null>(props.initialUserId ?? null);
const pageSize = ref(25);
const sorting = ref<Record<string, string>>({ created_at: 'desc' });

const entitySlug = computed(() => HISTORY_ENTITY_SLUG);

// Cursor (search_after) paging — Prev/Next without an offset (TABASCO-1HT).
const {
  cursor,
  pageNumber: currentPage,
  cachedTotal: total,
  lastPage,
  hasNext,
  hasPrev,
  reset: resetPager,
  captureMeta,
  navigate,
} = useCursorPager(pageSize);

const EMPTY_SCOPE: IScope = { id: 'root', scopeType: 'and', rules: [] };
const compositeFilter = computed(() => {
  if (props.seedKind === 'track') {
    return { ruleScopes: props.initialFilter ?? EMPTY_SCOPE };
  }
  return {
    ruleScopes: selectedUserId.value != null ? scopeForUser(selectedUserId.value) : EMPTY_SCOPE,
  };
});

// Listing every user needs the developer-gated `/api/permissions/users`
// endpoint; non-developers just see their own history with no picker.
const authStore = useAuthStore();
const canViewAllUsers = computed(() => authStore.userData?.is_developer ?? false);

// User dropdown options — fetched only when the picker is shown.
const { data: users, isLoading: usersLoading } = usePermissionUsers(
  computed(() => props.seedKind === 'user' && canViewAllUsers.value),
);
const userOptions = computed(() =>
  [...(users.value ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((user) => ({
      value: user.id,
      label: user.id === props.initialUserId ? `${user.name} (you)` : user.name,
    })),
);

// The inspector panel renders views with `<component :is>` and no `:key`, so a
// drill-down (or a fresh open) reuses this instance and only swaps props —
// re-sync the user picker to the seeded user when that happens.
watch(
  () => props.initialUserId,
  (nextUserId) => {
    selectedUserId.value = nextUserId ?? null;
  },
);
const locallyRetriedActionIds = ref(new Set<number>());

// Reset paging whenever the effective query changes (new track or new user).
watch(compositeFilter, () => {
  resetPager();
  locallyRetriedActionIds.value = new Set();
});

const { data, isLoading, error, refetch } = useSearchApi({
  entitySlug,
  cursor,
  pageSize,
  sorting,
  filter: compositeFilter,
});

// Immediate, because an answer that is already cached when this opens never
// changes after mount, and its total would never be read: "0 actions" over a
// list of five.
watch(data, () => captureMeta(data.value?.meta), { immediate: true });

const rows = computed<LiveTrackAction[]>(() => (data.value?.data ?? []) as LiveTrackAction[]);
// Gate on the returned cursor tokens (not the page bound), and never while a fetch is in flight.
const canPrev = computed(() => hasPrev.value && !isLoading.value);
const canNext = computed(() => hasNext.value && !isLoading.value);
const isEmpty = computed(() => !isLoading.value && !error.value && rows.value.length === 0);

const emptyMessage = computed(() =>
  props.seedKind === 'track'
    ? 'No actions recorded for this track yet.'
    : 'No actions recorded for this user yet.',
);

function onDrilldown(liveTrackId: number, title: string) {
  openTrackHistory(liveTrackId, title, { push: true });
}

/**
 * Refresh the list after a retry. The backend suppresses `can_retry` once a
 * later queued / in-flight action exists, but ES indexing is async so the new
 * row may not appear immediately — track the source id locally so the Retry
 * button hides right away.
 */
function onRetried(actionId: number) {
  locallyRetriedActionIds.value = new Set([...locallyRetriedActionIds.value, actionId]);
  void refetch();
}

function nextPage() {
  if (canNext.value) {
    navigate('next');
  }
}

function prevPage() {
  if (canPrev.value) {
    navigate('prev');
  }
}
</script>

<template>
  <!--
    On its own in the peek: the panel body already pads its content generously
    (`sm:py-10`). For this compact list that reads as dead space up top, so pull
    the content up into that padding and keep only a small gap below the header.
    Inside a record's tab, sit in the tab's normal flow instead.
  -->
  <div
    class="flex flex-col gap-3"
    :class="embedded ? 'pt-1' : 'px-3 pb-3 -mt-3 sm:-mt-7'"
    data-testid="history-inspector"
  >
    <!-- User picker — user-history mode, and only for users who may list everyone. -->
    <label
      v-if="seedKind === 'user' && canViewAllUsers"
      class="flex flex-col gap-1"
      data-testid="history-user-picker"
    >
      <span class="text-muted-foreground text-xs font-medium">User</span>
      <Select
        v-model="selectedUserId"
        :options="userOptions"
        option-label="label"
        option-value="value"
        :placeholder="usersLoading ? 'Loading users…' : 'Select a user'"
        class="text-sm"
      />
    </label>

    <p
      v-if="!isLoading && !error"
      class="text-muted-foreground text-xs"
      data-testid="history-result-count"
    >
      {{ total }} {{ total === 1 ? 'action' : 'actions' }}
    </p>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="text-muted-foreground flex flex-col items-center justify-center gap-2 py-10"
      data-testid="history-loading"
    >
      <ArrowPathIcon class="h-5 w-5 animate-spin" />
      <span class="text-xs">Loading actions…</span>
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      class="border-destructive/50 bg-destructive/10 text-destructive rounded-md border p-3 text-sm"
      data-testid="history-error"
    >
      Could not load track-action history. Try reopening the inspector.
    </div>

    <!-- Empty -->
    <div
      v-else-if="isEmpty"
      class="text-muted-foreground py-10 text-center text-sm"
      data-testid="history-empty"
    >
      {{ emptyMessage }}
    </div>

    <!-- Results -->
    <template v-else>
      <div class="flex flex-col gap-2">
        <LiveTrackActionCard
          v-for="action in rows"
          :key="action.id"
          :action="action"
          :retry-suppressed="locallyRetriedActionIds.has(action.id)"
          @drilldown="onDrilldown"
          @retried="onRetried"
        />
      </div>

      <div
        v-if="canPrev || canNext"
        class="flex items-center justify-between pt-1"
      >
        <Button
          variant="ghost"
          size="sm"
          type="button"
          class="h-7 px-2 text-xs"
          data-testid="history-prev-page"
          :disabled="!canPrev"
          @click="prevPage"
        >
          Previous
        </Button>
        <span class="text-muted-foreground text-xs tabular-nums">
          Page {{ currentPage }} / {{ lastPage }}
        </span>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          class="h-7 px-2 text-xs"
          data-testid="history-next-page"
          :disabled="!canNext"
          @click="nextPage"
        >
          Next
        </Button>
      </div>
    </template>
  </div>
</template>
